import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { AtualizacaoMovimento } from '../types';
import {
  MOCK_APURACAO_RESUMO,
  MOCK_APURACAO_MOVIMENTOS,
  MOCK_ACOMPANHAMENTO_MENSAL,
  MOCK_LANCAMENTO_OS,
} from '../dados-simulados';

/** FIN-060: resumo de apuração por DRE, para a competência escolhida. */
export function useApuracaoResumo(mes: string) {
  return useQuery({
    queryKey: ['financeiro', 'apuracao', 'resumo', mes],
    queryFn: async () => MOCK_APURACAO_RESUMO,
    enabled: mes.length > 0,
  });
}

/** FIN-061: grade de movimentos de uma DRE na competência — a base da edição em linha. */
export function useMovimentosDre(mes: string, dreId: string) {
  return useQuery({
    queryKey: ['financeiro', 'apuracao', 'movimentos', mes, dreId],
    queryFn: async () => MOCK_APURACAO_MOVIMENTOS,
    enabled: mes.length > 0 && dreId.length > 0,
  });
}

/**
 * Autosave da grade (FIN-061): PATCH com o delta, disparado pelo debounce de
 * ~800ms da tela. Optimistic UI fica na tela — este hook só faz a chamada e
 * invalida a lista da DRE/mês ao final, para reconciliar com o servidor.
 */
export function useAtualizarMovimento(mes: string, dreId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variaveis: { ordemServicoId: string; dados: AtualizacaoMovimento }) => {
      await new Promise((resolve) => setTimeout(resolve, 500));
      const linha = MOCK_APURACAO_MOVIMENTOS.find(
        (m) => m.ordemServicoId === variaveis.ordemServicoId,
      );
      if (linha) {
        const {
          diasOperados,
          diasFalta,
          kmRodados,
          diasAtivComplementarPresencial,
          ajusteValor,
          status,
        } = variaveis.dados;
        if (diasOperados !== undefined) linha.diasOperados = diasOperados;
        if (diasFalta !== undefined) linha.diasFalta = diasFalta;
        if (kmRodados !== undefined) linha.kmRodados = kmRodados.toFixed(2);
        if (diasAtivComplementarPresencial !== undefined) {
          linha.diasAtivComplementarPresencial = diasAtivComplementarPresencial;
        }
        if (ajusteValor !== undefined) linha.ajusteValor = ajusteValor.toFixed(2);
        if (status !== undefined) linha.status = status;
      }
      return { ok: true as const };
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: ['financeiro', 'apuracao', 'movimentos', mes, dreId],
      });
      void queryClient.invalidateQueries({ queryKey: ['financeiro', 'apuracao', 'resumo', mes] });
    },
  });
}

/** Aba "Acompanhamento Mensal" do hub de Apuração — ciclo por credenciado. */
export function useAcompanhamentoMensal(mes: string) {
  return useQuery({
    queryKey: ['financeiro', 'apuracao', 'acompanhamento', mes],
    queryFn: async () => MOCK_ACOMPANHAMENTO_MENSAL,
    enabled: mes.length > 0,
  });
}

/** FIN-062: formulário de lançamento de uma OS numa competência. */
export function useLancamentoOs(osId: string, _mes: string) {
  return useQuery({
    queryKey: ['financeiro', 'apuracao', 'lancamento', osId, _mes],
    queryFn: async () => MOCK_LANCAMENTO_OS[osId] ?? null,
    enabled: osId.length > 0,
  });
}

export function useSalvarLancamentoOs(osId: string, mes: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (_dados: Record<string, unknown>) => {
      await new Promise((resolve) => setTimeout(resolve, 500));
      return { ok: true as const };
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: ['financeiro', 'apuracao', 'lancamento', osId, mes],
      });
    },
  });
}
